import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { publicApi } from '../../../config/api';

const MarketplaceAssetForm = ({ onClose, onSubmit, initialData = null }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    category: initialData?.category || 'vehicle',
    description: initialData?.description || '',
    valuation_usd: initialData?.valuation_usd || '',
    price_btc: initialData?.price_btc || '',
    location: initialData?.location || '',
    contact_info: initialData?.contact_info || '',
    external_links: initialData?.external_links || [],
    specifications: initialData?.specifications || {}
  });
  
  const [newLink, setNewLink] = useState('');
  const [newSpecKey, setNewSpecKey] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [btc1Price, setBtc1Price] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  
  const categories = [
    { id: 'vehicle', label: 'Vehicle (Car, Motorcycle, etc.)', emoji: '🚗' },
    { id: 'yacht', label: 'Yacht/Boat', emoji: '⛵' },
    { id: 'property', label: 'Real Estate', emoji: '🏠' },
    { id: 'luxury', label: 'Luxury Items (Watch, Jewelry, etc.)', emoji: '💎' },
    { id: 'equipment', label: 'Equipment/Machinery', emoji: '🔧' },
    { id: 'other', label: 'Other', emoji: '📦' }
  ];
  
  // Fetch BTC1 price on mount
  useEffect(() => {
    const fetchBTC1Price = async () => {
      try {
        const response = await publicApi.get('/v1/quick-swap/btc1-price/');
        if (response.data && response.data.price) {
          setBtc1Price(response.data.price);
        } else {
          // Fallback price
          setBtc1Price(108);
        }
      } catch (error) {
        console.error('Failed to fetch BTC1 price:', error);
        setBtc1Price(108); // Fallback price
      } finally {
        setLoadingPrice(false);
      }
    };
    
    fetchBTC1Price();
  }, []);
  
  // Auto-calculate BTC1 price when USD valuation changes
  useEffect(() => {
    if (formData.valuation_usd && btc1Price && !initialData) {
      const usdValue = parseFloat(formData.valuation_usd);
      if (usdValue > 0) {
        const btcPrice = (usdValue / btc1Price).toFixed(8);
        setFormData(prev => ({
          ...prev,
          price_btc: btcPrice
        }));
      }
    }
  }, [formData.valuation_usd, btc1Price, initialData]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleAddLink = () => {
    if (!newLink.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      external_links: [...prev.external_links, newLink.trim()]
    }));
    setNewLink('');
  };
  
  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      external_links: prev.external_links.filter((_, i) => i !== index)
    }));
  };
  
  const handleAddSpec = () => {
    if (!newSpecKey.trim() || !newSpecValue.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [newSpecKey.trim()]: newSpecValue.trim()
      }
    }));
    setNewSpecKey('');
    setNewSpecValue('');
  };
  
  const handleRemoveSpec = (key) => {
    setFormData(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return {
        ...prev,
        specifications: newSpecs
      };
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please enter a description');
      return;
    }
    
    if (!formData.valuation_usd || parseFloat(formData.valuation_usd) <= 0) {
      toast.error('Please enter a valid USD valuation');
      return;
    }
    
    if (!formData.price_btc || parseFloat(formData.price_btc) <= 0) {
      toast.error('Please enter a valid BTC1 price');
      return;
    }
    
    if (!formData.location.trim()) {
      toast.error('Please enter a location');
      return;
    }
    
    if (!formData.contact_info.trim()) {
      toast.error('Please enter contact information');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        valuation_usd: parseFloat(formData.valuation_usd),
        price_btc: parseFloat(formData.price_btc)
      };
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-3xl w-full max-h-[70vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#010615] border-b border-gray-700 p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-white">
            {initialData ? 'Edit' : 'Create'} Listing
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., 2020 Lamborghini Aventador SVJ"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Category <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.emoji} {cat.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Detailed description of your asset..."
              rows={6}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              required
            />
          </div>
          
          {/* Pricing */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                USD Valuation <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.valuation_usd}
                onChange={(e) => handleInputChange('valuation_usd', e.target.value)}
                placeholder="100000"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Price in BTC1 <span className="text-red-400">*</span>
                <span className="text-white text-xs ml-2">(Auto-calculated)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.0001"
                  value={formData.price_btc}
                  readOnly
                  placeholder={loadingPrice ? "Loading..." : "10.5000"}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none cursor-not-allowed"
                  required
                />
                {btc1Price && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-400">
                    @ ${btc1Price}
                  </div>
                )}
              </div>
              <p className="text-xs text-white mt-1">
                Based on current BTC1 price of ${btc1Price || '...'}
              </p>
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Location <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="e.g., Miami, Florida, USA"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>
          
          {/* Contact Info */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Contact Information <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.contact_info}
              onChange={(e) => handleInputChange('contact_info', e.target.value)}
              placeholder="Phone: +1234567890
WhatsApp: +1234567890
Email: seller@example.com
Telegram: @username"
              rows={4}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              required
            />
            <p className="text-xs text-white mt-1">
              Buyers will see this information to contact you
            </p>
          </div>
          
          {/* External Links */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Photos & Videos Links
            </label>
            <div className="space-y-2">
              {formData.external_links.map((link, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={link}
                    readOnly
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(index)}
                    className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
              
              <div className="flex space-x-2">
                <input
                  type="url"
                  value={newLink}
                  onChange={(e) => setNewLink(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLink())}
                  placeholder="https://example.com/photo.jpg"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddLink}
                  className="px-4 py-2 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Specifications */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Specifications
            </label>
            <div className="space-y-2">
              {Object.entries(formData.specifications).map(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={key}
                    readOnly
                    className="w-1/3 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                  />
                  <input
                    type="text"
                    value={value}
                    readOnly
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSpec(key)}
                    className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newSpecKey}
                  onChange={(e) => setNewSpecKey(e.target.value)}
                  placeholder="Key (e.g., Year)"
                  className="w-1/3 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <input
                  type="text"
                  value={newSpecValue}
                  onChange={(e) => setNewSpecValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSpec())}
                  placeholder="Value (e.g., 2020)"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddSpec}
                  className="px-4 py-2 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Submit Buttons */}
          <div className="flex space-x-4 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : initialData ? 'Update Listing' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarketplaceAssetForm;

